import { AssortmentTypes, ConditionState, PackageType } from '@retail/calculus/types';
import {
  LevelToggleButtonGroup,
  ItemSearchFilter,
  StateToggleButton,
  StatusToggleButton,
} from '@retail/calculus/components';
import { SupplierBase } from '@retail/suppliers/types';
import { ProductGroupFilter, SideFilters } from '@retail/components';
import { useDisclosure } from '@shared/hooks';
import { DiscountGroup, HierarchyLevels, ProductGroupCount } from '@retail/products/types';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { calculusFilters, CalculusFilters } from '@retail/products/utils';
import { SelectDiscountGroup } from '@retail/my-assortment/components';
import { useFilters } from '@retail/products/hooks';
import { SelectSupplier } from '@retail/suppliers/components';

interface Props {
  productGroups?: ProductGroupCount[];
  discountGroups: DiscountGroup[];
  conditionState: ConditionState;
  assortmentType: AssortmentTypes;
  productGroupNumber: string | null;
  discountGroupCode: string | null;
  itemSearchQuery: string | null;
  availableHierarchyLevels?: HierarchyLevels[];
  availablePackageTypes?: PackageType[];
  selectedHierarchyLevel?: HierarchyLevels | PackageType;
  showFilters: boolean;
  isLoadingContent: boolean;
  supplierNumber?: number;
  supplierSearch?: {
    suppliers: SupplierBase[];
    selectedSupplier: SupplierBase | null;
    selectSupplierById: (supplierId: number | null) => void;
  };
}

export function CalculusFilters({
  productGroups,
  discountGroups,
  conditionState,
  assortmentType,
  productGroupNumber,
  discountGroupCode,
  itemSearchQuery,
  availableHierarchyLevels,
  availablePackageTypes,
  selectedHierarchyLevel,
  showFilters,
  isLoadingContent,
  supplierNumber,
  supplierSearch,
}: Props) {
  const t = useCalculusTFunction();
  const { setFilters } = useFilters<CalculusFilters>(calculusFilters);

  const { isOpen: showProductGroups, onToggle: toggleShowProductGroups } = useDisclosure(true);

  return (
    <SideFilters showFilters={showFilters}>
      {supplierSearch && (
        <SelectSupplier
          suppliers={supplierSearch.suppliers}
          getSupplierId={(supplier) => supplier.supplierNumber}
          getSupplierName={(supplier) => supplier.name}
          selectedSupplier={supplierSearch.selectedSupplier?.supplierNumber ?? null}
          selectSupplier={supplierSearch.selectSupplierById}
          hideSupplierId
        />
      )}
      <StateToggleButton
        conditionState={conditionState}
        setConditionState={(state: ConditionState) =>
          setFilters({ conditionState: state === 'ACTIVE' ? undefined : state })
        }
      />
      <StatusToggleButton
        assortmentType={assortmentType}
        setAssortmentType={(status: AssortmentTypes) =>
          setFilters({ assortmentType: status === 'BASE' ? undefined : status })
        }
      />

      <LevelToggleButtonGroup
        existingLevels={availableHierarchyLevels}
        availablePackageTypes={availablePackageTypes}
        selectedHierarchyLevel={selectedHierarchyLevel}
        isLoading={isLoadingContent}
        setSelectedHierarchyLevel={(level?: HierarchyLevels | PackageType) =>
          setFilters({ hierarchyLevel: level })
        }
      />

      <ItemSearchFilter
        query={itemSearchQuery}
        setQuery={(query?: string) => setFilters({ itemSearchQuery: query })}
      />
      {supplierNumber && (
        <SelectDiscountGroup
          discountGroups={discountGroups}
          selectedDiscountGroup={discountGroupCode}
          selectDiscountGroup={(groupCode) =>
            setFilters({ discountGroupCode: groupCode ? groupCode + '' : undefined })
          }
          getDiscountGroupId={(discountGroup) => discountGroup?.code ?? null}
        />
      )}

      <ProductGroupFilter
        productGroups={productGroups}
        selectedProductGroup={
          productGroups?.find((pg) => pg.groupNumber === productGroupNumber) ?? null
        }
        showProductGroups={showProductGroups}
        noGroupsMessage={t('calculus.noProductGroupConditions')}
        toggleShowProductGroups={toggleShowProductGroups}
        getCount={(productGroup) => productGroup.count}
        selectProductGroup={(groupNumber: string | null) =>
          setFilters({ productGroupNumber: groupNumber ?? undefined })
        }
      />
    </SideFilters>
  );
}
